import { Controller } from "@hotwired/stimulus";

// Drives a Knox in-page tabset. Clicking a tab reveals its matching pane and
// hides the rest; the active tab gets the `active` class for the underline.
//
// Same general logic as the card component's select-pane controller, adapted to
// our markup: panes are matched to tabs by id (a tab's href is `#<pane id>`) and
// hidden individually via the `hidden` attribute, and we delegate the click off
// the tablist so nested tab content (icon/title/badge) still resolves to the tab.
export default class extends Controller {
  static targets = ["tablist"]

  connect() {
    this.tabs = Array.from(this.tablistTarget.querySelectorAll("a.knox-tab__item"))
    this.panes = Array.from(this.element.querySelectorAll(":scope > .knox-tabs__pane"))
    this.panesById = this.panes.reduce((byId, pane) => ({ ...byId, [pane.id]: pane }), {})

    // Mise en place: honor an already-active tab, otherwise default to the first.
    const activeTab = this.tabs.find(tab => tab.classList.contains("active"))
    this.show(activeTab ? this.paneId(activeTab) : this.panes[0]?.id)
  }

  select(event) {
    const tab = event.target.closest("a.knox-tab__item")
    if (!tab || !this.tablistTarget.contains(tab)) return

    // These are in-page anchors; we drive the panes ourselves rather than jump.
    event.preventDefault()
    this.show(this.paneId(tab))
  }

  show(id) {
    if (!(id in this.panesById)) return

    this.panes.forEach(pane => { pane.hidden = pane.id !== id })
    this.tabs.forEach(tab => tab.classList.toggle("active", this.paneId(tab) === id))
  }

  // A tab links to its pane by anchor, so its href is `#<pane id>`.
  paneId(tab) {
    return (tab.getAttribute("href") || "").replace(/^#/, "")
  }
}
